import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Divider,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Home as HomeIcon } from '@mui/icons-material';

/**
 * PageHeader component for consistent page headers
 */
const PageHeader = ({
  title,
  description,
  breadcrumbs = [],
  actions = null,
  chips = [],
  icon = null,
  backLink = null
}) => {
  const theme = useTheme();
  const location = useLocation();
  
  // Generate breadcrumbs from current path if not provided
  const generatedBreadcrumbs = React.useMemo(() => {
    if (breadcrumbs.length > 0) return breadcrumbs;
    
    const paths = location.pathname.split('/').filter(Boolean);
    return paths.map((path, index) => {
      const url = `/${paths.slice(0, index + 1).join('/')}`;
      const label = path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, ' ');
      return { label, url };
    });
  }, [location.pathname, breadcrumbs]);
  
  return (
    <Box sx={{ mb: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        sx={{ 
          mb: 1,
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }
        }}
      >
        <Link
          component={RouterLink}
          to="/dashboard"
          color="inherit"
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            '&:hover': {
              color: theme.palette.primary.main
            }
          }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Home
        </Link>
        
        {generatedBreadcrumbs.map((crumb, index) => {
          const isLast = index === generatedBreadcrumbs.length - 1;
          
          return isLast ? (
            <Typography 
              key={crumb.url} 
              color="text.primary" 
              sx={{ 
                fontWeight: 'medium',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {crumb.label}
            </Typography>
          ) : (
            <Link
              key={crumb.url}
              component={RouterLink}
              to={crumb.url}
              color="inherit"
              sx={{ 
                '&:hover': {
                  color: theme.palette.primary.main
                },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
      
      {/* Title and actions */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {icon && (
            <Box 
              sx={{ 
                mr: 1.5, 
                color: theme.palette.primary.main,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {icon}
            </Box>
          )}
          
          <Typography 
            variant="h5" 
            component="h1" 
            sx={{ 
              fontWeight: 'bold',
              color: theme.palette.mode === 'dark' ? 'white' : '#1a2c4e',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: -4,
                left: 0,
                width: '40px',
                height: '3px',
                background: theme.palette.primary.main,
                borderRadius: '3px'
              }
            }}
          >
            {title}
          </Typography>
        </Box>
        
        {actions && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {actions}
          </Box>
        )}
      </Box>
      
      {/* Description */}
      {description && (
        <Typography 
          variant="body2" 
          sx={{ 
            mt: 1, 
            color: 'text.secondary',
            maxWidth: '800px'
          }}
        >
          {description}
        </Typography>
      )}
      
      {/* Chips */}
      {chips.length > 0 && (
        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {chips.map((chip, index) => (
            <Chip
              key={index}
              label={chip.label}
              color={chip.color || 'primary'}
              size="small"
              icon={chip.icon}
              onClick={chip.onClick}
              sx={{ 
                fontWeight: 'medium',
                '& .MuiChip-label': {
                  px: 1.5
                }
              }}
            />
          ))}
        </Box>
      )}
      
      <Divider sx={{ mt: 2, mb: 3 }} />
    </Box>
  );
};

PageHeader.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  breadcrumbs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired
    })
  ),
  actions: PropTypes.node,
  chips: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
      icon: PropTypes.node,
      onClick: PropTypes.func
    })
  ),
  icon: PropTypes.node,
  backLink: PropTypes.string
};

export default PageHeader;
